import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Chip,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  People as PeopleIcon,
  Chat as ChatIcon,
  Message as MessageIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Key as KeyIcon,
  Settings as SettingsIcon,
  Dashboard as DashboardIcon,
  EventNote as CalendarIcon,
  NotificationsActive as NotificationIcon,
  CloudUpload as UploadIcon,
  CloudDownload as DownloadCloudIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import { useTelegramStatus } from '../hooks/useTelegram';

const Dashboard = ({ token }) => {
  const [stats, setStats] = useState({
    contacts: 0,
    chats: 0,
    messages: 0,
    groups: 0,
    channels: 0,
    private_chats: 0,
    online: false,
  });
  
  const [loading, setLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);
  const [systemStatus, setSystemStatus] = useState({
    api_connected: false,
    telegram_connected: false,
    last_update: null,
  });
  
  const navigate = useNavigate();
  const { status: telegramStatus } = useTelegramStatus();

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch contacts
      let contactsCount = 0;
      try {
        const contactsRes = await axios.get(`${config.API_BASE}/list-contacts`, { headers });
        if (contactsRes.data?.contacts) {
          contactsCount = contactsRes.data.contacts.length;
        } else if (Array.isArray(contactsRes.data)) {
          contactsCount = contactsRes.data.length;
        }
      } catch (error) {
        console.log('Could not fetch contacts:', error.message);
      }
      
      // Fetch chats
      let chatsCount = 0;
      let groupsCount = 0;
      let channelsCount = 0;
      let privateCount = 0;
      try {
        const chatsRes = await axios.get(`${config.API_BASE}/list-chats`, { headers });
        if (chatsRes.data?.chats) {
          const chats = chatsRes.data.chats;
          chatsCount = chats.length;
          groupsCount = chats.filter(c => c.type === 'group' || c.type === 'supergroup').length;
          channelsCount = chats.filter(c => c.type === 'channel').length;
          privateCount = chats.filter(c => c.type === 'private').length;
        } else if (Array.isArray(chatsRes.data)) {
          chatsCount = chatsRes.data.length;
        }
      } catch (error) {
        console.log('Could not fetch chats:', error.message);
      }
      
      // Fetch recent messages (from first available chat)
      let recentMessages = [];
      try {
        const chatsRes = await axios.get(`${config.API_BASE}/list-chats`, { headers });
        if (chatsRes.data?.chats?.[0]) {
          const firstChat = chatsRes.data.chats[0];
          const messagesRes = await axios.get(
            `${config.API_BASE}/chats/list-messages/all`,
            { 
              headers,
              params: { chat_id: firstChat.id }
            }
          );
          if (messagesRes.data?.messages) {
            recentMessages = messagesRes.data.messages.slice(0, 5).map(msg => ({
              id: msg.id,
              text: msg.text || '(Media)',
              sender: msg.sender_name || 'Unknown',
              time: new Date(msg.date || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            }));
          }
        }
      } catch (error) {
        console.log('Could not fetch recent messages:', error.message);
      }
      
      // Update stats
      const newStats = {
        contacts: contactsCount,
        chats: chatsCount,
        messages: recentMessages.length, // Just for demo
        groups: groupsCount,
        channels: channelsCount,
        private_chats: privateCount,
        online: telegramStatus?.is_logged_in || false,
      };
      
      setStats(newStats);
      setRecentActivity(recentMessages);
      setSystemStatus({
        api_connected: true,
        telegram_connected: newStats.online,
        last_update: new Date().toLocaleTimeString(),
      });
      
      toast.success('Dashboard updated');
    } catch (error) {
      toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchDashboardData();
      // Auto-refresh every 60 seconds
      const interval = setInterval(fetchDashboardData, 60000);
      return () => clearInterval(interval);
    }
  }, [token, telegramStatus]);

  const handleExportData = () => {
    try {
      const ws = XLSX.utils.json_to_sheet([
        { Metric: 'Contacts', Count: stats.contacts },
        { Metric: 'Total Chats', Count: stats.chats },
        { Metric: 'Groups', Count: stats.groups },
        { Metric: 'Channels', Count: stats.channels },
        { Metric: 'Private Chats', Count: stats.private_chats },
        { Metric: 'Connection Status', Count: stats.online ? 'Online' : 'Offline' },
        { Metric: 'Last Updated', Count: systemStatus.last_update },
      ]);
      
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Dashboard Stats');
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(data, `telegram_dashboard_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Data exported successfully!');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const statCards = [
    {
      title: 'Total Contacts',
      value: stats.contacts,
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      color: '#3b82f6',
      progress: Math.min((stats.contacts / 100) * 100, 100),
      description: 'Telegram contacts',
      action: () => navigate('/contacts'),
    },
    {
      title: 'Active Chats',
      value: stats.chats,
      icon: <ChatIcon sx={{ fontSize: 40 }} />,
      color: '#10b981',
      progress: Math.min((stats.chats / 50) * 100, 100),
      description: `${stats.groups} groups, ${stats.channels} channels`,
      action: () => navigate('/chats'),
    },
    {
      title: 'Recent Messages',
      value: recentActivity.length,
      icon: <MessageIcon sx={{ fontSize: 40 }} />,
      color: '#8b5cf6',
      progress: Math.min((recentActivity.length / 10) * 100, 100),
      description: 'Last 5 messages',
      action: () => navigate('/messages'),
    },
    {
      title: 'Connection',
      value: stats.online ? 'Online' : 'Offline',
      icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
      color: stats.online ? '#10b981' : '#ef4444',
      progress: stats.online ? 100 : 0,
      description: stats.online ? 'Telegram connected' : 'Disconnected',
      action: () => navigate('/settings'),
    },
  ];

  const quickActions = [
    {
      title: 'Send Broadcast',
      description: 'Send message to multiple chats',
      icon: <MessageIcon />,
      color: '#3b82f6',
      action: () => toast.info('Broadcast feature coming soon'),
    },
    {
      title: 'Backup Data',
      description: 'Export all data',
      icon: <DownloadCloudIcon />,
      color: '#10b981',
      action: handleExportData,
    },
    {
      title: 'Token Manager',
      description: 'Manage API tokens',
      icon: <KeyIcon />,
      color: '#8b5cf6',
      action: () => navigate('/token-manager'),
    },
    {
      title: 'Settings',
      description: 'Configure app settings',
      icon: <SettingsIcon />,
      color: '#f59e0b',
      action: () => navigate('/settings'),
    },
  ];

  const systemHealth = [
    {
      label: 'API Connection',
      status: systemStatus.api_connected ? 'healthy' : 'error',
      value: systemStatus.api_connected ? 'Connected' : 'Disconnected',
    },
    {
      label: 'Telegram Connection',
      status: systemStatus.telegram_connected ? 'healthy' : 'error',
      value: systemStatus.telegram_connected ? 'Online' : 'Offline',
    },
    {
      label: 'Last Update',
      status: 'info',
      value: systemStatus.last_update || 'Never',
    },
    {
      label: 'Active Token',
      status: token ? 'healthy' : 'error',
      value: token ? 'Valid' : 'Invalid',
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Dashboard Overview
          </Typography>
          <Typography color="textSecondary">
            Welcome to your Telegram Management Dashboard
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Tooltip title="Refresh Data">
            <IconButton
              onClick={fetchDashboardData}
              disabled={loading}
              sx={{ 
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportData}
            sx={{ borderRadius: 2 }}
          >
            Export Data
          </Button>
        </Box>
      </Box>

      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              onClick={card.action}
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 3,
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)',
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      {card.title}
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700 }}>
                      {card.value}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      {card.description}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    color: card.color,
                    bgcolor: `${card.color}20`,
                    p: 1.5,
                    borderRadius: 2,
                  }}>
                    {card.icon}
                  </Box>
                </Box>
                
                <LinearProgress
                  variant="determinate"
                  value={card.progress}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: card.color,
                      borderRadius: 3,
                    },
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Left Column - Recent Activity & Quick Actions */}
        <Grid item xs={12} lg={8}>
          {/* Recent Activity */}
          <Card sx={{ borderRadius: 3, mb: 3, bgcolor: 'background.paper' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <NotificationIcon /> Recent Activity
                </Typography>
                <Chip 
                  label="Live" 
                  color="success" 
                  size="small" 
                  icon={<SpeedIcon fontSize="small" />}
                />
              </Box>
              
              {recentActivity.length > 0 ? (
                <List sx={{ p: 0 }}>
                  {recentActivity.map((activity, index) => (
                    <React.Fragment key={activity.id}>
                      <ListItem
                        sx={{
                          borderRadius: 2,
                          mb: 1,
                          bgcolor: 'rgba(255, 255, 255, 0.02)',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.05)',
                          },
                        }}
                      >
                        <ListItemIcon>
                          <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                            {activity.sender.charAt(0)}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {activity.sender}
                            </Typography>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" color="textSecondary" sx={{ maxWidth: '70%' }}>
                                {activity.text}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {activity.time}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < recentActivity.length - 1 && <Divider variant="inset" component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <MessageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography color="textSecondary">
                    No recent messages found
                  </Typography>
                  <Button
                    variant="outlined"
                    sx={{ mt: 2 }}
                    onClick={() => navigate('/messages')}
                  >
                    Go to Messages
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card sx={{ borderRadius: 3, bgcolor: 'background.paper' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <DashboardIcon /> Quick Actions
              </Typography>
              
              <Grid container spacing={2}>
                {quickActions.map((action, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Paper
                      onClick={action.action}
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          bgcolor: 'rgba(255, 255, 255, 0.05)',
                          borderColor: action.color,
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Box sx={{ 
                          color: action.color,
                          bgcolor: `${action.color}20`,
                          p: 1,
                          borderRadius: 2,
                        }}>
                          {action.icon}
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {action.title}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {action.description}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - System Status */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ borderRadius: 3, bgcolor: 'background.paper', height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <SecurityIcon /> System Status
              </Typography>
              
              <List sx={{ p: 0 }}>
                {systemHealth.map((item, index) => (
                  <React.Fragment key={index}>
                    <ListItem sx={{ px: 0, py: 1.5 }}>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.label}
                          </Typography>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: item.status === 'healthy' ? '#10b981' : 
                                         item.status === 'error' ? '#ef4444' : '#3b82f6',
                              }}
                            />
                            <Typography variant="body2" color="textSecondary">
                              {item.value}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < systemHealth.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
              
              <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(59, 130, 246, 0.1)', borderRadius: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
                  System Tips
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.875rem' }}>
                  • Dashboard auto-refreshes every 60 seconds
                  <br />
                  • Click on stats cards for detailed views
                  <br />
                  • Export data regularly for backups
                  <br />
                  • Monitor connection status here
                </Typography>
              </Box>
              
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchDashboardData}
                disabled={loading}
                sx={{ mt: 3, borderRadius: 2 }}
              >
                {loading ? 'Updating...' : 'Refresh Now'}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Bottom Info Bar */}
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="textSecondary">
          Last updated: {systemStatus.last_update || 'Never'}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Powered by Telegram API • v{config.VERSION}
        </Typography>
      </Box>
    </Box>
  );
};

export default Dashboard;